import React, { useCallback, useContext, useState } from "react";
import { useI18n } from "@core/i18n/i18n";
import { e2eTestId } from "@e2e/test-ids";
import DeleteIcon from "@assets/icons/delete.svg";
import DuplicateIcon from "@assets/icons/duplicate.svg";
import EditIcon from "@assets/icons/edit-file.svg";
import EyeVisibleIcon from "@assets/icons/eye-visible.svg";
import StopIcon from "@assets/icons/hand-stop.svg";
import { useNavigate } from "@core/routing/use-navigate";
import { useStateParams } from "@core/routing/use-state-params";
import { isNotNil } from "@utils/assert";
import { useProgramActions } from "@ui/pages/program-builder/hooks/use-program-actions";
import { useUser } from "@domain/user/hooks/use-user";
import { DialogContext, ModalId } from "@ui/components/dialog/dialog-provider";
import { RoutesPath } from "@ui/navigation/routes";
import { sendAnalyticsEventViewTemplate } from "@domain/template/template-analytics";
import {
	AnalyticsProgramClickType,
	AnalyticsProgramLocation,
	sendAnalyticsEventDeleteProgram,
	sendAnalyticsEventStopProgram,
	sendAnalyticsEventViewProgram,
} from "@domain/program/program-analytics";
import { PatientProgram } from "@domain/program/patient-program";
import { TemplateItem } from "@domain/template/template";
import { ProgramStatus } from "@domain/program/program-status/program-status";

interface PatientIds {
	id: string;
	analyticsId: string;
}

type CardAction = {
	destructive?: boolean;
	text: string;
	icon: React.JSX.Element;
	testId: string;
	onClick: () => void;
};

export function useProgramCard(): {
	lastProgramAdded: string | undefined;
	lastTemplateAdded: string | undefined;
	navigateToProgram: (patient: PatientIds, program: PatientProgram, fromCardActions?: boolean) => void;
	navigateToTemplate: (template: TemplateItem, fromCardActions?: boolean) => void;
	getProgramCardActions: (
		patient: PatientIds,
		program: PatientProgram,
		onDuplicateAsTemplate?: () => void,
	) => CardAction[];
	getTemplateCardActions: (template: TemplateItem) => CardAction[];
} {
	const { format } = useI18n();
	const navigate = useNavigate();
	const dialogContext = useContext(DialogContext);
	const user = useUser();
	const { duplicateAsDraftProgram, duplicateAsTemplateProgram } = useProgramActions();
	const [lastProgramAdded, setLastProgramAdded] = useState<string>();
	const { newProgramId: lastTemplateAdded } = useStateParams<RoutesPath.ProgramList>();

	const navigateToProgram = useCallback(
		(patient: PatientIds, program: PatientProgram, fromCardActions?: boolean) => {
			sendAnalyticsEventViewProgram(
				patient.analyticsId,
				program,
				fromCardActions ? AnalyticsProgramClickType.PROGRAM_LIST_DETAIL : AnalyticsProgramClickType.PROGRAM_LIST_CLICK,
			);
			navigate(RoutesPath.ProgramBuilder, {
				route: { patientId: patient.id },
				query: { programId: program.id },
			});
		},
		[navigate],
	);

	const navigateToTemplate = useCallback(
		(template: TemplateItem, fromCardActions?: boolean) => {
			sendAnalyticsEventViewTemplate(template.id, fromCardActions);
			navigate(RoutesPath.TemplateBuilder, { query: { templateId: template.id } });
		},
		[navigate],
	);

	const getProgramCardActions = useCallback(
		(patient: PatientIds, program: PatientProgram, onDuplicateAsTemplate?: () => void): CardAction[] => {
			const canEdit = program.status === ProgramStatus.Draft || program.status === ProgramStatus.Scheduled;
			const canStop = program.status === ProgramStatus.Ongoing;

			return [
				{
					text: canEdit ? format("global.programOperation.edit") : format("global.programOperation.see"),
					icon: canEdit ? <EditIcon /> : <EyeVisibleIcon />,
					onClick: () => navigateToProgram(patient, program, true),
					testId: e2eTestId.programTab.programMenu.goToBuilderButton,
				},
				{
					text: format("global.programOperation.duplicateAsDraft"),
					icon: <DuplicateIcon />,
					onClick: () => {
						duplicateAsDraftProgram({
							patientId: patient.id,
							programName: program.name,
							programId: program.id,
							onSuccess: setLastProgramAdded,
						});
					},
					testId: e2eTestId.programTab.programMenu.duplicateAsDraftButton,
				},
				{
					text: format("global.programOperation.duplicateAsTemplate"),
					icon: <DuplicateIcon />,
					onClick: () => {
						dialogContext?.openModal(ModalId.DuplicateAsTemplateProgram, {
							programName: program.name,
							onSubmit: async (templateName: string) => {
								duplicateAsTemplateProgram({
									patientId: patient.id,
									programId: program.id,
									templateName: templateName,
								});
								dialogContext.closeAll();
								onDuplicateAsTemplate?.();
							},
						});
					},
					testId: e2eTestId.programTab.programMenu.duplicateAsTemplateButton,
				},
				canStop && isNotNil(user)
					? {
							destructive: true,
							text: format("global.programOperation.stop"),
							icon: <StopIcon />,
							onClick: () => {
								dialogContext?.openModal(ModalId.StopProgram, {
									onSuccess: () =>
										sendAnalyticsEventStopProgram(patient, program.id, AnalyticsProgramLocation.PATIENT_DETAIL),
									program,
									patient,
									editor: user,
								});
							},
							testId: e2eTestId.programTab.programMenu.stopProgramButton,
						}
					: {
							destructive: true,
							text: format("global.programOperation.delete"),
							icon: <DeleteIcon />,
							onClick: () => {
								dialogContext?.openModal(ModalId.DeleteProgram, {
									patient,
									program,
									onSuccess: () =>
										sendAnalyticsEventDeleteProgram(patient, program.id, AnalyticsProgramLocation.PATIENT_DETAIL),
								});
							},
							testId: e2eTestId.programTab.programMenu.deleteProgramButton,
						},
			];
		},
		[dialogContext, duplicateAsDraftProgram, duplicateAsTemplateProgram, format, navigateToProgram, user],
	);

	const getTemplateCardActions = useCallback(
		(template: TemplateItem): CardAction[] => [
			{
				text: format("templateListing.templateCard.option.edit"),
				icon: <EditIcon />,
				testId: e2eTestId.createTemplatePage.templateMenu.goToBuilderButton,
				onClick: () => navigateToTemplate(template, true),
			},
			{
				destructive: true,
				text: format("templateBuilder.popupMenu.deleteTemplate"),
				icon: <DeleteIcon />,
				testId: e2eTestId.createTemplatePage.templateMenu.deleteTemplateButton,
				onClick: () => {
					dialogContext?.openModal(ModalId.DeleteTemplate, {
						templateId: template.id,
						templateName: template.name,
					});
				},
			},
		],
		[format, navigateToTemplate, dialogContext],
	);

	return {
		lastProgramAdded,
		lastTemplateAdded,
		navigateToProgram,
		navigateToTemplate,
		getProgramCardActions,
		getTemplateCardActions,
	};
}
